import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, AtSign, CheckCheck, MessageSquare, Search, Smile } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import { useChatConnection } from "../../../contexts/ChatConnectionContext";
import {
    ChatActivity,
    getRecentChatActivity,
    markAllChatActivityRead,
    markChatActivityRead,
} from "../../../services/chatService/ChatService";
import { getTimeAgo } from "../../../utilities/dateUtils";

const getActivitySummary = (activity: ChatActivity): string => {
    if (activity.activityType !== "chat.reaction") return activity.summary;
    const actor = activity.actorDisplayName?.trim() || "Someone";
    const reaction = activity.reactionType?.trim();
    return `${actor} reacted${reaction ? ` ${reaction}` : ""} to one of your messages.`;
};

const ChatActivityView: React.FC = () => {
    const { currentUser } = useAuth();
    const { connection, connectionVersion } = useChatConnection();
    const navigate = useNavigate();
    const [activities, setActivities] = useState<ChatActivity[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filter, setFilter] = useState<"all" | "mentions" | "replies" | "reactions">("all");
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const refresh = useCallback(async () => {
        if (!currentUser) return;
        setIsLoading(true);
        try {
            const data = await getRecentChatActivity(await currentUser.getIdToken(), 1, 30);
            setActivities(data);
            setPage(1);
            setHasMore(data.length === 30);
        } finally {
            setIsLoading(false);
        }
    }, [currentUser]);

    useEffect(() => { void refresh(); }, [refresh, connectionVersion]);

    useEffect(() => {
        if (!connection) return;
        const receive = (activity: ChatActivity) => setActivities(previous => [activity, ...previous.filter(item => item.id !== activity.id)]);
        const remove = (activityId: number) => setActivities(previous => previous.filter(item => item.id !== activityId));
        connection.on("ReceiveChatActivity", receive);
        connection.on("RemoveChatActivity", remove);
        return () => {
            connection.off("ReceiveChatActivity", receive);
            connection.off("RemoveChatActivity", remove);
        };
    }, [connection]);

    useEffect(() => {
        const handleActivityRead = (event: Event) => {
            const detail = (event as CustomEvent<{ activityId?: number; messageId?: number; readActivityIds?: number[]; all?: boolean }>).detail;
            const readIds = new Set(detail?.readActivityIds ?? []);
            setActivities(previous => previous.map(activity =>
                detail?.all || readIds.has(activity.id) || activity.id === detail?.activityId || activity.messageId === detail?.messageId
                    ? { ...activity, read: true }
                    : activity
            ));
        };
        window.addEventListener("chat-activity-read", handleActivityRead);
        return () => window.removeEventListener("chat-activity-read", handleActivityRead);
    }, []);

    const openActivity = async (activity: ChatActivity) => {
        if (!activity.read && currentUser) {
            setActivities(previous => previous.map(item => item.id === activity.id ? { ...item, read: true } : item));
            window.dispatchEvent(new CustomEvent("chat-activity-read", { detail: { activityId: activity.id } }));
            void markChatActivityRead(await currentUser.getIdToken(), activity.id);
        }
        const threadQuery = activity.threadRootMessageId ? `?thread=${activity.threadRootMessageId}&message=${activity.messageId}` : `?message=${activity.messageId}`;
        navigate(`/chat/${activity.conversationId}${threadQuery}`);
    };

    const markAllRead = async () => {
        if (!currentUser) return;
        setActivities(previous => previous.map(item => ({ ...item, read: true })));
        window.dispatchEvent(new CustomEvent("chat-activity-read", { detail: { all: true } }));
        await markAllChatActivityRead(await currentUser.getIdToken());
    };

    const loadMore = async () => {
        if (!currentUser || loadingMore) return;
        const nextPage = page + 1;
        setLoadingMore(true);
        try {
            const data = await getRecentChatActivity(await currentUser.getIdToken(), nextPage, 30);
            setActivities(previous => [...previous, ...data.filter(item => !previous.some(existing => existing.id === item.id))]);
            setPage(nextPage);
            setHasMore(data.length === 30);
        } finally {
            setLoadingMore(false);
        }
    };

    const iconFor = (activity: ChatActivity) => {
        if (activity.activityType === "chat.reaction") return <Smile size={18} />;
        if (activity.activityType === "chat.mention") return <AtSign size={18} />;
        return <MessageSquare size={18} />;
    };

    const filteredActivities = useMemo(() => activities.filter(activity => {
        if (unreadOnly && activity.read) return false;
        if (filter === "mentions" && activity.activityType !== "chat.mention") return false;
        if (filter === "replies" && activity.activityType !== "chat.thread_reply") return false;
        if (filter === "reactions" && activity.activityType !== "chat.reaction") return false;
        return !query.trim() || getActivitySummary(activity).toLowerCase().includes(query.trim().toLowerCase());
    }), [activities, filter, query, unreadOnly]);

    return (
        <section className="dark-page-canvas flex min-h-0 flex-1 flex-col bg-slate-50/70 dark:bg-slate-950">
            <header className="dark-surface shrink-0 bg-white px-5 py-4 dark:bg-slate-900 sm:px-7">
                <div className="mx-auto flex max-w-5xl items-start gap-3">
                    <span className="dark-accent-tile mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-[#7E57C2] dark:bg-purple-400/15 dark:text-purple-300"><Activity size={18} /></span>
                    <div className="min-w-0 flex-1">
                        <h1 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">Activity</h1>
                        <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">Mentions, reactions, and replies in threads you participate in</p>
                    </div>
                {activities.some(activity => !activity.read) && (
                    <button type="button" onClick={() => void markAllRead()} className="inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-[#6D4DB3] hover:bg-purple-50 dark:text-purple-300 dark:hover:bg-purple-400/10">
                        <CheckCheck size={16} /> Mark all read
                    </button>
                )}
                </div>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-7">
                <div className="mx-auto max-w-5xl">
                    <div className="dark-surface rounded-2xl bg-white p-3 shadow-[0_8px_24px_rgba(15,23,42,0.06)] dark:bg-slate-900 dark:shadow-[0_14px_36px_rgba(0,0,0,0.22)]">
                        <label className="chat-search-field flex items-center gap-2 rounded-xl bg-slate-100 px-3 dark:bg-slate-800">
                            <Search size={15} className="text-slate-400" />
                            <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Filter activity" className="chat-search-input h-9 min-w-0 flex-1 border-0 bg-transparent text-xs text-slate-800 outline-none ring-0 shadow-none placeholder:text-slate-400 dark:text-slate-100" />
                        </label>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            {([['all', 'All'], ['mentions', 'Mentions'], ['replies', 'Replies'], ['reactions', 'Reactions']] as const).map(([value, label]) => (
                                <button key={value} type="button" onClick={() => setFilter(value)} className={`rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition ${filter === value ? "bg-purple-100 text-[#6847A3] dark:bg-purple-400/15 dark:text-purple-200" : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"}`}>{label}</button>
                            ))}
                            <button type="button" onClick={() => setUnreadOnly(current => !current)} className={`ml-auto rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition ${unreadOnly ? "bg-purple-100 text-[#6847A3] dark:bg-purple-400/15 dark:text-purple-200" : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"}`}>Unread only</button>
                        </div>
                    </div>

                    <div className="mt-3 space-y-1.5">
                    {isLoading ? (
                        Array.from({ length: 6 }).map((_, index) => <div key={index} className="dark-surface flex animate-pulse gap-3 rounded-xl bg-white px-3 py-3 dark:bg-slate-900"><div className="h-9 w-9 rounded-xl bg-slate-200 dark:bg-slate-800" /><div className="flex-1"><div className="h-3 w-3/4 rounded bg-slate-200 dark:bg-slate-800" /><div className="mt-2 h-2.5 w-24 rounded bg-slate-100 dark:bg-slate-800/60" /></div></div>)
                    ) : filteredActivities.length === 0 ? (
                        <div className="py-16 text-center text-sm text-slate-500 dark:text-slate-400">{activities.length === 0 ? "You’re all caught up." : "No activity matches these filters."}</div>
                    ) : filteredActivities.map(activity => (
                        <button
                            key={activity.id}
                            type="button"
                            onClick={() => void openActivity(activity)}
                            className={`flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left shadow-[0_2px_10px_rgba(15,23,42,0.04)] transition dark:shadow-none ${activity.read ? "dark-surface bg-white hover:bg-slate-100/70 dark:bg-slate-900 dark:hover:bg-slate-800" : "bg-purple-50 hover:bg-purple-100/70 dark:bg-purple-400/10 dark:hover:bg-purple-400/15"}`}
                        >
                            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[#7E57C2] dark:text-purple-300 ${activity.read ? "bg-slate-100 dark:bg-slate-800" : "bg-white/80 dark:bg-purple-400/10"}`}>{iconFor(activity)}</span>
                            <span className="min-w-0 flex-1">
                                <span className="block text-[13px] leading-5 text-slate-700 dark:text-slate-200">{getActivitySummary(activity)}</span>
                                <span className="mt-0.5 block text-[10px] text-slate-400 dark:text-slate-500">{getTimeAgo(activity.createdAt)}</span>
                            </span>
                            {!activity.read && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#7E57C2]" />}
                        </button>
                    ))}
                    {hasMore && !isLoading && <button type="button" onClick={() => void loadMore()} disabled={loadingMore} className="flex h-10 w-full items-center justify-center gap-2 rounded-xl text-xs font-semibold text-[#6847A3] hover:bg-purple-50 dark:text-purple-300 dark:hover:bg-purple-400/10">{loadingMore ? "Loading…" : "Load older activity"}</button>}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ChatActivityView;
